import { Component } from 'react';
import {
    FTUE_DIRECTIVE_EVENT,
    FTUE_DIRECTIVE_CLEAR_EVENT,
} from '../Insights/InsightsWindow';

/**
 * Plays a scripted FTUE scene (a server-driven sequence of steps) by
 * pushing one step at a time into the Desktop Insights Window's
 * directive slot.
 *
 * Pacing model: the server fires all steps in a single burst when it
 * dispatches a scene. The runner queues every incoming FTUE_STEP and
 * renders one at a time, advancing on the user's click. Steps are
 * deduped by `${scene_key}:${order}` so a duplicate burst (multiple
 * channels, retries, etc.) doesn't re-queue work the user has already
 * walked through. Only the final step in a scene sends an FTUE_ACK
 * back to the server; the ack causes the server to record the scene
 * as seen and skip re-emitting on future triggers.
 *
 * Render output is null — the visible directive UI (message body +
 * Next/Got it button) lives in the Insights Window. Steps reach it over
 * the FTUE_DIRECTIVE_EVENT window event; the directive payload carries
 * this runner's handleAdvance as its onAdvance callback so the button
 * click drives the queue.
 *
 * Self-registers on app.ftueSceneRunner (the live path the PackageRouter
 * forwards FTUE_STEP to) and on FtueController via setSceneRunner (the
 * controller-mediated reach path; left in place as a known reference
 * even though directive UI no longer flows through the controller).
 * StrictMode-safe: componentWillUnmount only detaches the handles.
 */
export default class FtueSceneRunner extends Component {
    constructor(props) {
        super(props);
        this.state = {
            sceneKey: null,
            order: 0,
            isFinal: false,
        };
        // Non-state buffers — queue order and dedupe set don't drive
        // render output directly; the active step (in state) does.
        this.queue = [];
        this.seenSteps = new Set();
    }

    componentDidMount() {
        const app = this.props.app;
        app.ftueSceneRunner = this;
        if (app.ftueController) {
            app.ftueController.setSceneRunner(this);
        }
    }

    componentWillUnmount() {
        // Detach only — no navigation, no other side effects.
        const app = this.props.app;
        if (app.ftueSceneRunner === this) {
            app.ftueSceneRunner = null;
        }
        if (app.ftueController
            && app.ftueController.sceneRunner === this) {
            app.ftueController.setSceneRunner(null);
        }
    }

    stepId = (payload) => `${payload.scene_key}:${payload.order}`;

    playStep = (payload) => {
        if (!payload) return;
        const id = this.stepId(payload);
        if (this.seenSteps.has(id)) {
            // Duplicate burst (multi-channel fanout, retry, etc.) —
            // ignore. The user already saw or queued this step.
            return;
        }
        this.seenSteps.add(id);
        if (this.state.sceneKey === null) {
            // Nothing showing — render this step now.
            this.renderStep(payload);
        } else {
            // Something is on screen — queue this step for after the
            // user clicks Next.
            this.queue.push(payload);
        }
    };

    renderStep = (payload) => {
        // Push the directive into the Insights Window's directive slot.
        // The window renders the body + advance button; clicking it calls
        // back through the onAdvance callback carried in the payload.
        window.dispatchEvent(new CustomEvent(FTUE_DIRECTIVE_EVENT, {
            detail: {
                message: payload.message,
                scene_key: payload.scene_key,
                order: payload.order,
                is_final: payload.is_final,
                onAdvance: this.handleAdvance,
            },
        }));
        this.setState({
            sceneKey: payload.scene_key,
            order: payload.order,
            isFinal: payload.is_final,
        });
    };

    handleAdvance = () => {
        if (this.state.isFinal) {
            // The package.data.<field> shape matches the inbound
            // convention the Django consumer reads (see set_volume etc.).
            this.props.app.state.authenticator.sendMessage(
                'FTUE_ACK', { data: { scene_key: this.state.sceneKey } });
            // Empty the Insights Window's directive slot. Without this the
            // final step's body + button strand in the window — the slot
            // only clears when something tells it to.
            window.dispatchEvent(
                new CustomEvent(FTUE_DIRECTIVE_CLEAR_EVENT));
            // Scene finished — reset everything, including the queue,
            // so any straggler steps from a duplicate burst are dropped.
            this.queue = [];
            this.seenSteps.clear();
            this.setState({ sceneKey: null, order: 0, isFinal: false });
            return;
        }
        if (this.queue.length > 0) {
            this.renderStep(this.queue.shift());
        }
    };

    render() {
        // No visible UI of its own — the directive body + advance button
        // render inside the Insights Window. This component stays mounted
        // purely for its queue / dedup / ack state machine and its
        // app.ftueSceneRunner / FtueController registrations.
        return null;
    }
}
